import React from "react";
import { Modal, Button } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { Icon } from "@/shared/components/Icon/Icon";

const StyledModal = styled(Modal)`
	.ant-modal-content {
		background: #f3f5ff !important;
		border-radius: 24px !important;
		padding: 32px !important;
		box-shadow: 0px 12px 24px 0px #1559a53d !important;
	}
`;

const ModalContainer = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	gap: 16px;
`;

const CloseButton = styled.button`
	position: absolute;
	top: -8px;
	right: -8px;
	width: 32px;
	height: 32px;
	border: none;
	border-radius: 50%;
	background: transparent;
	color: #555;
	cursor: pointer;
	display: inline-flex;
	align-items: center;
	justify-content: center;

	&:hover:not(:disabled) {
		background: rgba(0, 0, 0, 0.06);
	}

	&:disabled {
		cursor: not-allowed;
		opacity: 0.5;
	}
`;

const Title = styled.div`
	font-size: 24px;
	font-weight: 600;
`;

const Description = styled.div`
	font-size: 16px;
	color: #454545;
`;

const ButtonContainer = styled.div`
	display: flex;
	gap: 12px;
	margin-top: 8px;
`;

const DiscardButton = styled(Button)`
	flex: 1;
	height: 48px;
	border-radius: 24px;
	border: 2px solid #3a0ca3;
	color: #3a0ca3;
	font-weight: 600;

	&:hover,
	&:focus-visible {
		box-shadow: inset 0 0 0 2px #3a0ca3 !important;
		border-color: #3a0ca3 !important;
		color: #3a0ca3 !important;
	}

	&:disabled,
	&.ant-btn-disabled {
		border-color: #d9d9d9 !important;
		color: #8c8c8c !important;
		background: #f5f5f5 !important;
		cursor: not-allowed;
		opacity: 1 !important;
		box-shadow: none !important;
	}
`;

const SaveButton = styled(Button)`
	flex: 1;
	height: 48px;
	border-radius: 24px;
	background: #3a0ca3 !important;
	color: #fff !important;
	font-weight: 600;
	border: none;

	&:hover {
		background: #3a0ca3 !important;
		opacity: 0.9;
	}

	&:disabled,
	&.ant-btn-disabled {
		background: #d9d9d9 !important;
		border-color: #d9d9d9 !important;
		color: #8c8c8c !important;
		cursor: not-allowed;
		opacity: 1 !important;
	}
`;

interface SaveChangesConfirmModalProps {
	open: boolean;
	onCancel: () => void;
	onDiscard: () => void;
	onSave: () => void;
	canSave?: boolean;
	saving?: boolean;
}

export const SaveChangesConfirmModal: React.FC<SaveChangesConfirmModalProps> = ({
	open,
	onCancel,
	onDiscard,
	onSave,
	canSave = true,
	saving = false,
}) => {
	return (
		<StyledModal
			title={null}
			open={open}
			onCancel={saving ? undefined : onCancel}
			footer={null}
			closable={false}
			maskClosable={!saving}
			keyboard={!saving}
			centered
			width={520}
		>
			<ModalContainer>
				<CloseButton aria-label="Close" onClick={onCancel} disabled={saving}>
					<CloseOutlined />
				</CloseButton>
				<Icon src="/icon/save.svg" size={27} color="#3a0ca3" />
				<Title>Save Changes</Title>
				<Description>
					Campaign details have been changed.
					<br />
					Do you want to save changes?
				</Description>
				<ButtonContainer>
					<DiscardButton onClick={onDiscard} disabled={saving}>
						Discard and Close
					</DiscardButton>
					<SaveButton type="primary" onClick={onSave} disabled={!canSave || saving} loading={saving}>
						Save
					</SaveButton>
				</ButtonContainer>
			</ModalContainer>
		</StyledModal>
	);
};
